import {Injectable} from '@angular/core';
import {Observable, catchError, map} from 'rxjs';
import {HttpParams} from '@angular/common/http';
import {HttpService} from '../http.service';
import {HttpErrorHandlerService} from '../http-error-handler.service';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';
import {endpoints} from '../../../environments/endpoints';
import { AppStorageService } from '../app-storage.service';
import { StorageKeys } from '../storage-keys';

@Injectable({
  providedIn: 'root'
})
export class ArticlesService {
  private apiUrl = `${environment.api.articles}`;

  constructor(
    private httpService: HttpService,
    private errorHandler: HttpErrorHandlerService,
    private storage: AppStorageService,
    private translate: TranslateService
  ) {
  }

  /**
   * Obtiene la lista de artículos filtrados por categoría y subcategoría (opcional)
   * @param page Número de página actual (por defecto 1)
   * @param perPage Cantidad de elementos por página (por defecto 10)
   * @param sortField Campo por el que ordenar (por defecto 'created_at')
   * @param sortOrder Dirección de ordenamiento (1: ascendente, -1: descendente)
   * @returns Observable con los artículos y metadata para paginación
   */
  index(page: number = 1, perPage: number = 10, sortField: string = 'created_at', sortOrder: number = -1): Observable<any> {
    // Obtenemos los IDs de categoría y subcategoría desde el storage
    const categoryObj = this.storage.getJSON<any>(StorageKeys.SELECTED_CATEGORY);
    const subcategoryObj = this.storage.getJSON<any>(StorageKeys.SELECTED_SUBCATEGORY);

    if (!categoryObj) {
      throw new Error(this.translate.instant('ARTICLES.NO_CATEGORY_SELECTED'));
    }

    // Verificamos que categoryObj existe y tiene un id válido
    // Permitimos que id sea 0 (para "Ver todo") pero no undefined o null
    if (categoryObj.id === undefined || categoryObj.id === null) {
      throw new Error(this.translate.instant('ARTICLES.INVALID_CATEGORY_ID'));
    }

    const category_id = categoryObj.id;
    const subcategory_id = subcategoryObj?.id || null;

    // Construimos los parámetros de la consulta
    const orderDir = sortOrder === 1 ? 'asc' : 'desc';
    let params = `?category_id=${category_id}&page=${page}&per_page=${perPage}&sort=${sortField}&order=${orderDir}`;

    // Añadimos subcategory_id solo si existe
    if (subcategory_id) {
      params += `&subcategory_id=${subcategory_id}`;
    }

    return this.httpService.get(`${this.apiUrl}${params}`).pipe(
      map((response: any) => {
        // Verificamos que la respuesta tiene la estructura esperada
        if (response.success && response.data) {
          return {
            articles: response.data.data || [],
            pagination: {
              currentPage: response.data.current_page,
              lastPage: response.data.last_page,
              perPage: response.data.per_page,
              total: response.data.total,
              hasMorePages: response.data.current_page < response.data.last_page,
              nextPageUrl: response.data.next_page_url  // Agregamos esta propiedad
            },
            message: response.message
          };
        } else {
          // Si no tiene la estructura esperada, devolvemos un objeto vacío
          return {
            articles: [],
            pagination: {
              currentPage: 1,
              lastPage: 1,
              perPage: perPage,
              total: 0,
              hasMorePages: false,
              nextPageUrl: null
            },
            message: response.message || this.translate.instant('ARTICLES.FETCH_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Método existente para obtener artículos sin filtros
   * @param page Número de página actual
   * @param perPage Cantidad de elementos por página
   * @returns Observable con los artículos y metadata para paginación
   */
  indexWithoutFilters(page: number, perPage: number): Observable<any> {
    return this.httpService.get(`${this.apiUrl}?page=${page}&per_page=${perPage}`).pipe(
      map((response: any) => {
        // Verificamos que la respuesta tiene la estructura esperada
        if (response.success && response.data) {
          return {
            articles: response.data.data || [],
            pagination: {
              currentPage: response.data.current_page,
              lastPage: response.data.last_page,
              perPage: response.data.per_page,
              total: response.data.total,
              hasMorePages: response.data.current_page < response.data.last_page,
              nextPageUrl: response.data.next_page_url  // Agregamos esta propiedad
            },
            message: response.message
          };
        } else {
          // Si no tiene la estructura esperada, devolvemos un objeto vacío
          return {
            articles: [],
            pagination: {
              currentPage: 1,
              lastPage: 1,
              perPage: perPage,
              total: 0,
              hasMorePages: false,
              nextPageUrl: null
            },
            message: response.message || this.translate.instant('ARTICLES.FETCH_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Nuevo método con soporte para filtros
   * @param params Objeto con los parámetros de filtrado
   * @returns Observable con los artículos filtrados y metadata para paginación
   */
  indexWithFilters(params: any): Observable<any> {
    // Construir queryString a partir del objeto de parámetros
    const queryParams = new HttpParams({fromObject: params});
    return this.httpService.get(`${this.apiUrl}?${queryParams.toString()}`).pipe(
      map((response: any) => {
        // Verificamos que la respuesta tiene la estructura esperada
        if (response.success && response.data) {
          return {
            articles: response.data.data || [],
            pagination: {
              currentPage: response.data.current_page,
              lastPage: response.data.last_page,
              perPage: response.data.per_page,
              total: response.data.total,
              hasMorePages: response.data.current_page < response.data.last_page,
              nextPageUrl: response.data.next_page_url  // Agregamos esta propiedad
            },
            message: response.message
          };
        } else {
          // Si no tiene la estructura esperada, devolvemos un objeto vacío
          return {
            articles: [],
            pagination: {
              currentPage: 1,
              lastPage: 1,
              perPage: params.per_page || 10,
              total: 0,
              hasMorePages: false,
              nextPageUrl: null
            },
            message: response.message || this.translate.instant('ARTICLES.FETCH_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Crea un nuevo artículo en el servidor
   * @param articleData Datos del artículo a crear
   * @returns Observable con la respuesta del servidor
   */
  create(articleData: any): Observable<any> {
    return this.httpService.post(`${this.apiUrl}`, articleData).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          return {
            article: response.data,
            success: true,
            message: response.message || this.translate.instant('ARTICLES.CREATE_SUCCESS')
          };
        } else {
          return {
            article: null,
            success: false,
            message: response.message || this.translate.instant('ARTICLES.CREATE_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Actualiza un artículo existente en el servidor
   * @param id ID del artículo a actualizar
   * @param articleData Datos actualizados del artículo
   * @returns Observable con la respuesta del servidor
   */
  update(id: number, articleData: any): Observable<any> {
    return this.httpService.put(`${this.apiUrl}/${id}`, articleData).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          return {
            article: response.data,
            success: true,
            message: response.message || this.translate.instant('ARTICLES.UPDATE_SUCCESS')
          };
        } else {
          return {
            article: null,
            success: false,
            message: response.message || this.translate.instant('ARTICLES.UPDATE_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Elimina un artículo del servidor
   * @param id ID del artículo a eliminar
   * @returns Observable con la respuesta del servidor
   */
  delete(id: number): Observable<any> {
    return this.httpService.delete(`${this.apiUrl}/${id}`).pipe(
      map((response: any) => {
        if (response.success) {
          return {
            success: true,
            message: response.message || this.translate.instant('ARTICLES.DELETE_SUCCESS')
          };
        } else {
          return {
            success: false,
            message: response.message || this.translate.instant('ARTICLES.DELETE_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene la lista de artículos pertenecientes al usuario autenticado
   * @param page Número de página actual (por defecto 1)
   * @param perPage Cantidad de elementos por página (por defecto 10)
   * @param sortField Campo por el que ordenar (por defecto 'created_at')
   * @param sortOrder Dirección de ordenamiento (1: ascendente, -1: descendente)
   * @returns Observable con los artículos del usuario y metadata para paginación
   */
  getUserArticles(page: number = 1, perPage: number = 10, sortField: string = 'created_at', sortOrder: number = -1): Observable<any> {
    // Construimos los parámetros de la consulta
    const orderDir = sortOrder === 1 ? 'asc' : 'desc';
    const params = `?page=${page}&per_page=${perPage}&sort=${sortField}&order=${orderDir}`;

    return this.httpService.get(`${this.apiUrl}/my-articles${params}`).pipe(
      map((response: any) => {
        // Verificamos que la respuesta tiene la estructura esperada
        if (response.success && response.data) {
          return {
            articles: response.data.data || [],
            pagination: {
              currentPage: response.data.current_page,
              lastPage: response.data.last_page,
              perPage: response.data.per_page,
              total: response.data.total,
              hasMorePages: response.data.current_page < response.data.last_page,
              nextPageUrl: response.data.next_page_url
            },
            message: response.message
          };
        } else {
          // Si no tiene la estructura esperada, devolvemos un objeto vacío
          return {
            articles: [],
            pagination: {
              currentPage: 1,
              lastPage: 1,
              perPage: perPage,
              total: 0,
              hasMorePages: false,
              nextPageUrl: null
            },
            message: response.message || this.translate.instant('ARTICLES.FETCH_USER_ARTICLES_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene detalles de un artículo específico por su ID
   * @param id ID del artículo a consultar
   * @returns Observable con los datos del artículo
   */
  getArticleById(id: number): Observable<any> {
    return this.httpService.get(`${this.apiUrl}/${id}`).pipe(
      map((response: any) => {
        if (response.success && response.data) {
          return {
            article: response.data,
            success: true,
            message: response.message || 'Artículo obtenido con éxito'
          };
        } else {
          return {
            article: null,
            success: false,
            message: response.message || this.translate.instant('ARTICLES.FETCH_SINGLE_ERROR')
          };
        }
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene el estado de destacación de un artículo
   * Respuesta esperada: { currently_featured: boolean, featured_until: string | null, article_id: number }
   */
  getFeaturedStatus(id: number): Observable<any> {
    return this.httpService.get(`${this.apiUrl}/${id}/featured-status`).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
        return { currently_featured: false, featured_until: null, article_id: id };
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }
}
